/**
 * What a research run looks like while it is running, as numbers rather than as JSX. F-147, F-149.
 *
 * **Its own module with no DOM and no React in it, for `plan/step.ts`'s reason**: a component
 * is a thousand lines of markup that needs a browser to render at all, and every judgement this
 * screen makes about a sixteen-minute wait would then be untestable. What is decided here is
 * the arithmetic — how many subjects are open, how much material has arrived, and where the
 * line of it goes — and every one of those is a number a Node test can assert.
 *
 * The complaint it answers, Leo, 2026-09-17: *"there is also no feedback at all in terms of
 * what is being read, overall i have no idea whats going on unless i refresh or move around, a
 * user would just drop here"*, and beside it: *"progress should be dynamic, if I refresh i see
 * character counts for research raising but nothing tells me"*. Both are about material that is
 * already on the rows the screen already polls. Nothing below asks for anything new.
 *
 * **Nothing here is a function of the clock except where the clock is the axis.** The arrival
 * curve's x-axis is time because the thing being drawn is when material landed; every number on
 * it comes from an `updatedAt` a pass actually wrote. No bar moves because time passed.
 */

/*
 * Types only, so nothing from `@nabu/shared` reaches the bundle or the Node test's runtime —
 * `plan/coverage.ts` explains why that matters, and it is the same barrel with the same
 * `node:crypto` behind it.
 */
import type { ResearchRunState, ResearchSubject, ResearchSubjectState } from '@nabu/shared';

/**
 * The five things that can be true of a subject, from the learner's side of the screen.
 *
 * Eight states collapse to five groups, and which states share a group is the whole of the
 * judgement. `outlined` and `queued` are both "not started"; `deep` and `thin` are both "there
 * is material" — a thin subject has a document and reaches the plan, which is what the learner
 * is counting. `barren` is separate from both and from failure, because the research ran and
 * the world was empty, which is a fact about the subject; `unfunded` and `failed` are facts
 * about us and are grouped away from it for the reason `KEEPS_ITS_STATE` exists in
 * `Research.tsx` — telling somebody nothing exists on a subject nobody managed to look for is
 * the one lie this screen must not tell.
 */
export type Standing = 'waiting' | 'reading' | 'found' | 'empty' | 'stalled';

/**
 * Which group each state falls in.
 *
 * **Duplicated from the pipeline's vocabulary, deliberately and unhappily**, for the reason
 * `subjectFraction` in `Research.tsx` carries a copy of `SUBJECT_PROGRESS`: the shared barrel
 * re-exports `keys.ts`, which imports `node:crypto`, so a *value* import from it would pull
 * that into a browser bundle. The type is imported, so a state added to the union without a
 * home here fails the build rather than rendering as nothing.
 */
const STANDING: Record<ResearchSubjectState, Standing> = {
  outlined: 'waiting',
  queued: 'waiting',
  researching: 'reading',
  deep: 'found',
  thin: 'found',
  barren: 'empty',
  unfunded: 'stalled',
  failed: 'stalled',
};

/**
 * Whether more research is still on its way for this objective.
 *
 * **The browser's copy of `researchStillComing` in `@nabu/shared`**, which the API and the
 * pipeline both answer this question through, and which a browser bundle cannot import as a
 * value for the reason `STANDING` above carries its own table. Two of the three components that
 * ask it were already answering it inline — `Research.tsx` computes `working` this way — so this
 * is one definition replacing two rather than a third opinion: the screen must not offer to
 * build a plan while material for it is still arriving (F-146), and the API refuses that with
 * `still_researching`, so a disagreement here is a button whose only outcome is a refusal.
 */
export function stillComing(state: ResearchRunState | undefined): boolean {
  return state === 'outlining' || state === 'deepening';
}

/**
 * How long a run may say nothing before this screen stops believing it is running.
 *
 * **A display rule, not a claim about the pipeline.** The run row is written as subjects settle,
 * and the longest single subject pass measured on this workspace was about twenty-five minutes,
 * so an hour of silence is not a slow subject — it is a run that stopped without saying so. The
 * pipeline has left runs at `deepening` for ever before, and `researchStillComing` in
 * `@nabu/shared` has no staleness window of its own.
 *
 * The direction this fails in is the whole reason for the number: believing a stale row withholds
 * the *build* button, which is the learner's only way forward, and nothing else on the screen
 * would say why it had gone.
 */
const STALE_RUN_MS = 60 * 60 * 1_000;

/**
 * Whether research is still arriving *and* the row saying so was written recently enough to mean it.
 *
 * `stillComing` answers the pipeline's question; this answers the screen's, which has one more
 * thing to worry about. A progress read that lands once saying `deepening` and then fails is kept
 * — deliberately, so a single failed request does not empty a panel mid-wait — and a kept reading
 * of a run that has stopped would withhold the offer to build for the rest of the session.
 *
 * An unreadable timestamp counts as stale for the same reason: every way of not knowing resolves
 * towards giving the learner their button back.
 */
export function researchArriving(
  run: { state?: ResearchRunState; updatedAt?: string } | undefined,
  now: number,
): boolean {
  if (!stillComing(run?.state)) return false;
  const touched = Date.parse(run?.updatedAt ?? '');
  if (!Number.isFinite(touched)) return false;
  return now - touched < STALE_RUN_MS;
}

export function standingOf(state: ResearchSubjectState): Standing {
  // `?? 'waiting'` because the state arrives from a DynamoDB row and is cast to the union
  // rather than validated against it. A row from a schema ahead of this deployment would
  // otherwise index to `undefined` and be counted in no group at all, so the totals on screen
  // would silently fail to add up to the number of subjects beside them.
  return STANDING[state] ?? 'waiting';
}

/** How the run stands, in the numbers the screen says out loud. */
export interface Pulse {
  subjects: number;
  waiting: number;
  reading: number;
  found: number;
  empty: number;
  stalled: number;
  /** Everything that will not change again: found, empty and stalled together. */
  settled: number;
  /** Characters of material written so far, across every subject. */
  characters: number;
  /** How many subjects said what they could not find. */
  gaps: number;
}

/**
 * The counts, in one pass.
 *
 * `settled` is derived here rather than at three call sites, because "finished" is exactly the
 * thing two screens disagreed about before: a `thin` subject is finished, a `barren` one is
 * finished, and a bar that waits for twelve `deep`s never completes.
 */
export function pulseOf(subjects: readonly ResearchSubject[]): Pulse {
  const pulse: Pulse = {
    subjects: subjects.length,
    waiting: 0,
    reading: 0,
    found: 0,
    empty: 0,
    stalled: 0,
    settled: 0,
    characters: 0,
    gaps: 0,
  };

  for (const subject of subjects) {
    const standing = standingOf(subject.state);
    pulse[standing]++;
    if (standing !== 'waiting' && standing !== 'reading') pulse.settled++;
    pulse.characters += Number.isFinite(subject.characters) ? subject.characters : 0;
    if (subject.missing) pulse.gaps++;
  }
  return pulse;
}

/** One subject as a bar: how much it found against the subject that found most. */
export interface Bar {
  subjectId: string;
  title: string;
  state: ResearchSubjectState;
  standing: Standing;
  characters: number;
  /** 0..1 against the largest subject, which is what makes twelve bars comparable. */
  fraction: number;
}

/**
 * The bars, scaled against the biggest subject rather than against a fixed ceiling.
 *
 * **Relative, because the absolute number is meaningless to a learner and the comparison is
 * not.** Nobody knows whether 21,609 characters is a lot; everybody can see that one subject
 * found ten times what another did, which is the honest shape of a research run and the thing
 * the character counts on the rows never said. The number itself is rendered beside the bar,
 * so the scale is never the only thing carrying it.
 *
 * A subject with nothing yet gets a fraction of nought and still gets a row: twelve rows of
 * which four are empty is what "eight still to come" looks like, and dropping them would make
 * the list shrink and grow as a run progressed.
 */
export function barsOf(subjects: readonly ResearchSubject[]): Bar[] {
  const most = subjects.reduce(
    (top, subject) => Math.max(top, Number.isFinite(subject.characters) ? subject.characters : 0),
    0,
  );
  return subjects.map((subject) => {
    const characters = Number.isFinite(subject.characters) ? subject.characters : 0;
    return {
      subjectId: subject.subjectId,
      title: subject.title,
      state: subject.state,
      standing: standingOf(subject.state),
      characters,
      fraction: most > 0 ? characters / most : 0,
    };
  });
}

/** A point on the arrival curve, both axes normalised to 0..1 so the SVG decides its own size. */
export interface Point {
  x: number;
  y: number;
}

export interface Arrival {
  points: Point[];
  /** The characters the last point stands for, so the chart can be labelled without a second sum. */
  characters: number;
  /** How long the run has been going, in milliseconds, for the axis label. */
  spanMs: number;
}

/**
 * Material arriving over the life of the run, rebuilt from the rows rather than from the session.
 *
 * **The obvious implementation is wrong and it is worth saying why.** Sampling the total every
 * time the page polls would draw a line that starts when the tab was opened: a learner who
 * refreshes — which is exactly what Leo did, and how he noticed the counts were rising at all —
 * loses the whole graph and watches it start again from nothing. Every subject row already
 * carries `updatedAt`, which for a settled subject is the moment it settled, so the curve of
 * what arrived when is a fact about the run and survives a reload, a second device and a week.
 *
 * It is a cumulative step curve and not a rate: the question a learner is asking at minute nine
 * is "is this still going", and a line that climbs answers it in a way no number does.
 *
 * Subjects that have not settled contribute nothing — their characters are written when the
 * pass returns, so there is no partial figure to plot — and a row whose timestamp cannot be
 * read is left out rather than placed at the epoch, which would drag the whole axis to 1970.
 *
 * **It is a picture of when material arrived and not of how long the machine ran, and the two
 * can differ honestly.** The 2026-09-17 execution took 16m16s; the curve drawn from that
 * objective's rows spans 121 minutes, because three of its subjects were re-researched by hand
 * two hours later to recover them and that moved their `updatedAt`. The long flat stretch in the
 * middle is that intervention, rendered faithfully. Neither number is the other's correction.
 */
export function arrivalCurve(input: {
  startedAt: string;
  subjects: readonly ResearchSubject[];
  /** Whether the run is still going, which decides where the right-hand edge is. */
  running: boolean;
  now: number;
}): Arrival {
  const start = Date.parse(input.startedAt);
  if (!Number.isFinite(start)) return { points: [], characters: 0, spanMs: 0 };

  const landings = input.subjects
    .filter((subject) => standingOf(subject.state) !== 'waiting')
    .filter((subject) => standingOf(subject.state) !== 'reading')
    .map((subject) => ({
      at: Date.parse(subject.updatedAt),
      characters: Number.isFinite(subject.characters) ? subject.characters : 0,
    }))
    .filter((landing) => Number.isFinite(landing.at) && landing.at >= start)
    .sort((left, right) => left.at - right.at);

  const characters = landings.reduce((total, landing) => total + landing.characters, 0);
  /*
   * The right-hand edge: now while the run is going, and the last thing that happened once it
   * is over. A finished run drawn against `now` is a curve that shuffles left every time it is
   * looked at, until a run from last week is a vertical line at the origin.
   */
  const last = landings.length > 0 ? landings[landings.length - 1]!.at : start;
  const end = input.running ? Math.max(input.now, last) : last;
  const spanMs = Math.max(end - start, 1);

  const points: Point[] = [{ x: 0, y: 0 }];
  let running = 0;
  for (const landing of landings) {
    running += landing.characters;
    /*
     * Two points per landing, which is what makes it a step rather than a diagonal. A straight
     * line between arrivals says material trickled in continuously; it arrives in one lump when
     * a subject's pass returns, and drawing the lie would make a stalled run look busy.
     */
    points.push({ x: (landing.at - start) / spanMs, y: characters > 0 ? (running - landing.characters) / characters : 0 });
    points.push({ x: (landing.at - start) / spanMs, y: characters > 0 ? running / characters : 0 });
  }
  /*
   * Carried flat to the edge, so the line reaches the right of the box and the gap since the
   * last arrival is visible as a plateau rather than as an absence.
   *
   * Skipped when the last landing is already there, which is every finished run: the right-hand
   * edge *is* the last arrival, and pushing it twice writes a zero-length segment into the path
   * that a rounded line cap renders as a dot past the end of the curve.
   */
  const edge = points[points.length - 1];
  if (!edge || edge.x < 1) points.push({ x: 1, y: characters > 0 ? 1 : 0 });

  return { points, characters, spanMs };
}

/**
 * The points as an SVG `points` attribute, y flipped because SVG counts downwards.
 *
 * Here rather than in the component so the geometry is asserted by a test: an off-by-one on the
 * flip draws a curve that falls as material arrives, which looks deliberate and is exactly
 * backwards.
 */
export function polyline(points: readonly Point[], width: number, height: number): string {
  return points
    .map((point) => `${(point.x * width).toFixed(2)},${((1 - point.y) * height).toFixed(2)}`)
    .join(' ');
}

/**
 * How many sources a pass looked at, out of the origin the document records.
 *
 * The research documents carry `origin.location` as `web_search:5 searches`, which is a field
 * written for an operator reading a content store and not for a learner watching a wait. The
 * half after the colon is the part that means something to them — it is the closest thing the
 * pipeline records to *"how many sources each found"*, which is what F-147 asks for — and it is
 * shown as the pass's own words rather than translated, for the reason every other pipeline
 * sentence on these screens is.
 *
 * Absent rather than guessed when the field is missing or shaped differently. A machine-shaped
 * string rendered raw under a quotation is worse than nothing at all there.
 */
export function sourcesOf(origin: string | undefined): string | undefined {
  if (!origin) return undefined;
  const after = origin.slice(origin.indexOf(':') + 1).trim();
  if (!origin.includes(':') || after === '') return undefined;
  // A bare number with no noun says nothing; anything that reads as a phrase is passed through.
  return /[a-z]/i.test(after) ? after : undefined;
}
